import { DatePickerButton } from "@/components/DatePickerButton";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useApi, errMsg, ago, siteToday } from "@/lib/api";

export const KrokPanel = () => {
  const api = useApi();
  const [d, setD] = useState(null);
  const [date, setDate] = useState(siteToday());
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api("get", "/admin/krok").then(setD).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api("get", "/meetings/dates").then((ds) => { const t = siteToday(); if (ds?.length && !ds.includes(t)) setDate(ds.find((x) => x > t) ? [...ds].reverse().find((x) => x > t) : ds[0]); }).catch(() => {});
  }, [api]);
  if (!d) return null;

  const sync = async () => {
    setBusy(true);
    try {
      const { id } = await api("post", "/admin/krok/sync", { date });
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const j = await api("get", `/admin/jobs/${id}`);
        if (j.state === "error") throw new Error(j.error);
        if (j.state === "done") {
          const added = (j.results || []).reduce((n, x) => n + (x.results_added || 0), 0);
          const miss = (j.results || []).filter((x) => !x.matched).map((x) => x.track);
          toast.success(`Odds updated${added ? `, ${added} result(s) filled in` : ""}${miss.length ? `. Not found on Krok: ${miss.join(", ")}` : ""}`);
          break;
        }
      }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false); load();
  };
  const toggle = async (auto) => {
    try { await api("put", "/admin/krok", { auto }); toast.success(auto ? "Silks & results will refresh hourly 7am–5pm" : "Automatic Krok refresh off"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="rounded-2xl bg-[#131C31] border border-white/5 p-5 space-y-3" data-testid="admin-krok-panel">
      <h2 className="font-display text-lg font-bold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-400" />Odds, silks & results · Krok Odds</h2>
      <p className="text-xs text-slate-500">
        {d.configured ? "Connected" : "API key not added"} · last update {d.last_sync ? ago(d.last_sync) : "never"}
        {d.credits_left != null && <> · <span data-testid="krok-credits" className={Number(d.credits_left) < 20 ? "text-amber-300" : ""}>{d.credits_left} credits left this month</span></>}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <DatePickerButton testid="krok-date-input" value={date} onChange={setDate} className="bg-[#0B101D] border border-white/10 rounded-full px-3 py-1.5 text-xs text-slate-100 hover:border-blue-500/50 transition-colors" />
        <button data-testid="krok-sync-button" onClick={sync} disabled={busy || !d.configured} className="px-4 py-1.5 rounded-full bg-blue-600 hover:bg-blue-500 text-xs font-semibold disabled:opacity-50">{busy ? "Updating…" : "Update silks & results"}</button>
      </div>
      <label className="flex items-center gap-3 text-sm">
        <span className="mr-auto">Refresh automatically every hour (7am–5pm)<span className="block text-[11px] text-slate-500">Uses about 7 credits per refresh (about 2,500 a month), well within your Krok premium allowance.</span></span>
        <Switch data-testid="krok-auto-toggle" checked={d.auto} onCheckedChange={toggle} disabled={!d.configured} />
      </label>
      {d.last_job?.state === "error" && <p className="text-xs text-rose-300" data-testid="krok-error">{d.last_job.error}</p>}
    </section>
  );
};
